import type { ApiErrorBody } from '@creativitas/shared';

/**
 * Thin fetch wrapper. Vite proxies `/api` to the Fastify server in dev, so the
 * client never needs to know the server origin.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & { body?: unknown };

/**
 * Сколько ждём ответ, прежде чем признать связь потерянной.
 *
 * Ограничения не было вовсе, и 7 сентября это стоило половины дня. У Дарьи
 * пропала часть пакетов до сервера: одни запросы дошли и получили ответ за
 * миллисекунды, другие не дошли совсем. Страница осталась висеть на серых
 * заглушках — навсегда, потому что `fetch` без сигнала не сдаётся никогда.
 * Снаружи это выглядело как сломанная платформа, и разбирались с этим по
 * логам задним числом, вместо того чтобы прочитать «связь потеряна» на
 * экране.
 *
 * Пятнадцать секунд — с большим запасом: сервер отвечает за единицы
 * миллисекунд, и даже на слабом мобильном канале столько уходить не должно.
 * Запрос повторяется один раз (`retry: 1` в App.tsx), так что до сообщения об
 * ошибке проходит около полуминуты. Повтор здесь ценен сам по себе: он
 * открывает новое соединение вместо застрявшего, а это ровно то, чего Дарья
 * добивалась руками, очищая данные сайта.
 */
const REQUEST_TIMEOUT_MS = 15_000;

/**
 * Сигнал отмены запроса: свой у того, кто его передал, иначе — по таймеру.
 *
 * `AbortSignal.timeout` есть во всех браузерах с 2022 года, но проверка
 * оставлена: без неё отсутствие метода уронило бы каждый запрос в приложении,
 * то есть лечение вышло бы хуже болезни.
 */
function requestSignal(given: RequestOptions['signal']): AbortSignal | undefined {
  if (given) return given;
  return typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
    ? AbortSignal.timeout(REQUEST_TIMEOUT_MS)
    : undefined;
}

/**
 * Обрыв связи, а не ответ сервера.
 *
 * Приходит как `ApiError` со статусом 0: у обрыва нет кода ответа, а весь
 * интерфейс уже умеет показывать `ApiError` через `toUserMessage`. Отмену,
 * заказанную самим вызывающим кодом, пропускаем как есть — она не ошибка
 * связи, и выдавать её за потерю сети значило бы врать.
 *
 * Отмена без сигнала вызывающего — это наш собственный таймер. Спецификация
 * называет её `TimeoutError`, но Safari сообщает `AbortError` с текстом
 * «Fetch is aborted», и 16–17 сентября Дарья видела на iPad именно эти слова
 * вместо объяснения. Поэтому различаем не по имени ошибки, а по тому, чей был
 * сигнал.
 */
function connectionError(cause: unknown, callerSignal: boolean): unknown {
  const name = cause instanceof Error ? cause.name : '';
  if (name === 'AbortError' && callerSignal) return cause;

  return name === 'TimeoutError' || name === 'AbortError'
    ? new ApiError(0, 'timeout', 'Сервер не ответил вовремя. Проверьте связь и повторите.')
    : new ApiError(0, 'offline', 'Не удалось связаться с сервером. Проверьте связь и повторите.');
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, signal, ...rest } = options;

  let response: Response;
  try {
    response = await fetch(path.startsWith('/api') ? path : `/api${path}`, {
      ...rest,
      signal: requestSignal(signal),
      credentials: 'include',
      headers: {
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...headers,
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (cause) {
    throw connectionError(cause, Boolean(signal));
  }

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get('content-type')?.includes('application/json') ?? false;
  /* Тело читается отдельно: связь рвётся и на полпути, уже после заголовков. */
  let payload: unknown;
  try {
    payload = isJson ? await response.json() : await response.text();
  } catch (cause) {
    throw connectionError(cause, Boolean(signal));
  }

  if (!response.ok) {
    const error = (payload as ApiErrorBody | undefined)?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'request_failed',
      error?.message ?? 'Запрос не выполнен',
      error?.details,
    );
  }

  return payload as T;
}

/** Turns any thrown value into a message that is safe to render (§55). */
export function toUserMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return 'Что-то пошло не так. Попробуйте ещё раз.';
}
